import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { colors } from '@guryou/colors';
import { FileTypes } from '@guryou/core';
import { imageNotFound, HtmlVideoPlayer, Modal as HtmlModal } from '@guryou/html-components';
import {
  generateWidgetPaymentIntent,
  getCustomerPublicFile,
  getUserVideoCourseFilesFeel,
  getVideoCourseFilesFeel,
  markFileSeenUnseen,
  resetGetFileState,
  stateIsLoaded,
  stateIsLoading,
  resetState,
  PROVIDER_GET_GENERAL_INFO,
  PROVIDER_LATEST_WIDGET,
  useEffectOnSuccess,
} from '@guryou/redux';
import { printPrice } from '@guryou/utils/build/stringUtils';
import { Icon } from '../../components/shared';
import Modal from '../../components/shared/Modal';
import AnimatedBackground from '../../components/shared/AnimatedBackground';
import MyCustomPreviewModal from 'modules/myVideos/MyCustomPreviewModal';
import MyImagePreview from 'modules/myVideos/MyImagePreview';

const VideoCourseFiles = ({ id, videoCourse, isEnrolled = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { i18n, t } = useTranslation();
  const authData = useSelector(state => state.auth?.data);
  const publicFilesState = useSelector(state => state.videoCourses.videoCourseFiles);
  const userFilesState = useSelector(state => state.videoCourses.userVideoCourseFiles);
  const vcSelector = isEnrolled ? userFilesState : publicFilesState;
  const [imageSrc, setImageSrc] = useState(videoCourse?.imageUrl);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [coursePreview, setCoursePreview] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      dispatch(isEnrolled ? getUserVideoCourseFilesFeel(id) : getVideoCourseFilesFeel(id));
    }
  }, [dispatch, id, isEnrolled]);

  const handleImageError = () => {
    setImageSrc(imageNotFound);
  };

  const handleProviderClick = () => {
    const providerId = videoCourse?.providerId;
    if (providerId) {
      dispatch(resetState(PROVIDER_GET_GENERAL_INFO));
      dispatch(resetState(PROVIDER_LATEST_WIDGET));
      return history.push(`/${i18n.language}/provider/${providerId}`);
    }
  };

  const checkIfLocked = (free, allowPreview) => {
    if (!allowPreview) return true;
    return false;
  };

  const handleFileSeenUnseen = (fileId, value) => {
    if (!isEnrolled) return;
    dispatch(markFileSeenUnseen(fileId, value));
  };

  const handleViewDetails = section => {
    setSelectedSection(section);
    setShowSectionModal(true);
  };

  const openCoursePreviewPanel = (section, activeFileId, sectionsList = []) => {
    if (!section?.files?.length) return;
    const firstAvailableFile =
      section.files.find(
        file => !checkIfLocked(file.free, isEnrolled) && (file.transcoded || [FileTypes.document, FileTypes.image].includes(file.type))
      ) || section.files[0];
    const active = activeFileId || firstAvailableFile?.id;
    const idx = sectionsList.findIndex(s => s.sectionId === section.sectionId);
    const sectionIndex = idx >= 0 ? idx + 1 : 1;
    const lessonCount = section.files?.length || 0;

    setCoursePreview({
      items: section.files || [],
      active,
      providerId: +(section?.providerId || videoCourse?.providerId),
      allowPreview: isEnrolled,
      handleFileSeenUnseen,
      title: section?.sectionName,
      sectionIndex,
      lessonCount,
    });
  };

  const closeSectionModal = () => {
    setShowSectionModal(false);
    setSelectedSection(null);
  };

  const handlePreviewFile = (file, section, sectionsList) => {
    const allowPreview = isEnrolled;
    const isLocked = checkIfLocked(file.free, allowPreview);
    const canPlay = file.transcoded || [FileTypes.document, FileTypes.image].includes(file.type);

    if (!canPlay || isLocked) return;

    dispatch(getCustomerPublicFile(file.id, +(section?.providerId || videoCourse?.providerId)));

    if (!file.seen && isEnrolled) {
      handleFileSeenUnseen(file.id, true);
    }

    if (file.type !== FileTypes.document) {
      openCoursePreviewPanel(section, file.id, sectionsList);
    }
  };

  const handleEnrollNow = () => {
    if (!videoCourse?.id) return;
    dispatch(
      generateWidgetPaymentIntent({
        productId: videoCourse.id,
        email: authData?.email || null,
        firstname: authData?.firstname || authData?.name || null,
        lastname: authData?.lastname || authData?.surname || null,
        phone: authData?.phone || null,
      })
    );
  };

  useEffectOnSuccess(
    state => state.files.getPublic,
    data => {
      if (data?.type === FileTypes.document) {
        window.open(data?.signedUrl, '_blank');
      }
      dispatch(resetGetFileState());
    }
  );

  if (!id || (stateIsLoaded(vcSelector) && !vcSelector.data)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icon name="exclamation-triangle" size="xl" className="text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('VIDEO_COURSE_PAGE.somethingWentWrong')}</h2>
          <p className="text-gray-600">{t('VIDEO_COURSE_PAGE.courseNotFound')}</p>
        </div>
      </div>
    );
  }

  const vfiles = (stateIsLoaded(vcSelector) && vcSelector?.data?.length && vcSelector.data) || [];
  const providerInitial = String(videoCourse?.providerName || '?')
    .trim()
    .charAt(0)
    .toUpperCase();

  // Show loading skeleton if video course data is not available
  if (stateIsLoading(vcSelector) && !videoCourse) {
    return (
      <div className="min-h-screen bg-white">
        {/* Hero Section Skeleton */}
        <div className="relative overflow-hidden" style={{ backgroundColor: colors.mineShaft }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Content Skeleton */}
              <div className="text-white space-y-8">
                {/* Category Badge Skeleton */}
                <div className="inline-flex items-center px-4 py-2 rounded-full">
                  <div className="w-4 h-4 bg-gray-600 rounded mr-2 animate-pulse"></div>
                  <div className="w-24 h-4 bg-gray-600 rounded animate-pulse"></div>
                </div>

                {/* Title Skeleton */}
                <div>
                  <div className="h-16 bg-gray-600 rounded-lg mb-4 animate-pulse"></div>
                  <div className="w-24 h-1 bg-gray-600 rounded-full animate-pulse"></div>
                </div>

                {/* Description Skeleton */}
                <div className="space-y-3 max-w-2xl">
                  <div className="h-4 bg-gray-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-gray-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-gray-600 rounded w-3/4 animate-pulse"></div>
                  <div className="h-4 bg-gray-600 rounded w-5/6 animate-pulse"></div>
                </div>

                {/* Stats and Info Skeleton */}
                <div className="flex flex-wrap items-center gap-8">
                  {/* Provider Info Skeleton */}
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-gray-600 mr-4 animate-pulse"></div>
                    <div>
                      <div className="w-20 h-3 bg-gray-600 rounded mb-2 animate-pulse"></div>
                      <div className="w-32 h-5 bg-gray-600 rounded animate-pulse"></div>
                    </div>
                  </div>

                  {/* Price Skeleton */}
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-gray-600 mr-4 animate-pulse"></div>
                    <div>
                      <div className="w-12 h-3 bg-gray-600 rounded mb-2 animate-pulse"></div>
                      <div className="w-20 h-8 bg-gray-600 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Image Skeleton */}
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="w-full max-w-lg h-80 bg-gray-600 rounded-3xl animate-pulse"></div>
                  {/* Corner Badge Skeleton */}
                  <div className="absolute top-6 right-6 bg-gray-600 rounded-2xl px-4 py-2">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-gray-500 rounded mr-2 animate-pulse"></div>
                      <div className="w-12 h-4 bg-gray-500 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Content Section Skeleton */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-300 rounded mb-4 animate-pulse"></div>
            <div className="w-20 h-1 bg-gray-300 rounded-full animate-pulse"></div>
          </div>

          <div className="max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Skeleton Loading */}
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Section Header Skeleton */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-xl animate-pulse"></div>
                      <div className="flex-1">
                        <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      </div>
                    </div>
                  </div>

                  {/* Lessons List Skeleton */}
                  <div className="p-6">
                    <div className="space-y-3">
                      {[1, 2, 3].map(j => (
                        <div key={j} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                          <div className="flex-1">
                            <div className="h-3 bg-gray-200 rounded w-4/5 animate-pulse mb-1"></div>
                            <div className="h-2 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                          </div>
                          <div className="w-6 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section Footer Skeleton */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <div className="flex items-center justify-end">
                      <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <AnimatedBackground>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Content */}
            <div className="text-white space-y-8">
              {/* Category Badge */}
              {videoCourse?.groupName && (
                <div
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium text-white shadow-lg backdrop-blur-sm border border-white/20"
                  style={{ backgroundColor: `#${videoCourse.groupColor}40` }}
                >
                  <Icon name="tag" size="sm" className="mr-2" />
                  {videoCourse.groupName}
                </div>
              )}

              {/* Title */}
              <div>
                <h1 className="text-5xl lg:text-6xl font-black mb-4 leading-tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  {videoCourse?.name}
                </h1>
                <div className="w-24 h-1 rounded-full" style={{ backgroundColor: 'var(--feel-primary)' }}></div>
              </div>

              {/* Description */}
              <div
                className="text-xl text-gray-300 leading-relaxed max-w-2xl max-h-48 overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-gray-300 [&_*]:text-gray-300 [&_p]:text-gray-300 [&_span]:text-gray-300 [&_div]:text-gray-300 [&_strong]:text-gray-300 [&_b]:text-gray-300 [&_em]:text-gray-300 [&_i]:text-gray-300"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#9CA3AF transparent',
                }}
                dangerouslySetInnerHTML={{ __html: videoCourse?.description }}
              />

              {/* Stats and Info */}
              <div className="flex flex-wrap items-center gap-8">
                {/* Provider Info */}
                <div className="flex items-center group">
                  <div
                    className="w-12 h-12 rounded-full mr-4 flex items-center justify-center text-white font-bold text-lg shadow-lg"
                    style={{
                      backgroundColor: 'color-mix(in srgb, var(--feel-primary) 45%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--feel-primary) 65%, transparent)',
                    }}
                  >
                    {providerInitial}
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">{t('VIDEO_COURSE_PAGE.createdBy')}</p>
                    <button
                      onClick={handleProviderClick}
                      className={`text-white hover:text-[var(--feel-primary)] transition-colors duration-200 font-semibold text-lg hover:underline`}
                    >
                      {videoCourse?.providerName}
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="flex items-center">
                  <div
                    className="w-12 h-12 rounded-full mr-4 shadow-lg flex items-center justify-center text-white font-bold text-lg"
                    style={{ backgroundColor: 'var(--feel-primary)' }}
                  >
                    €
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">{t('VIDEO_COURSE_PAGE.price')}</p>
                    <span className="text-3xl font-bold text-white">{printPrice(videoCourse?.price)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative group">
                {/* Glow Effect */}
                <div
                  className="absolute -inset-4 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      'linear-gradient(45deg, color-mix(in srgb, var(--feel-primary) 25%, transparent), color-mix(in srgb, var(--feel-primary-dark) 25%, transparent))',
                    filter: 'blur(20px)',
                  }}
                ></div>

                <div className="relative">
                  <img
                    src={imageSrc || videoCourse?.imageUrl}
                    alt={videoCourse?.name || 'Video course image'}
                    onError={handleImageError}
                    className="w-full max-w-lg rounded-3xl shadow-2xl border-4 border-white/20 transform group-hover:scale-105 transition-transform duration-500"
                  />

                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-black/30 rounded-3xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="bg-white/95 backdrop-blur-sm rounded-full p-8 shadow-2xl transform scale-75 group-hover:scale-100 transition-transform duration-300">
                      <Icon name="play" size="xl" className="text-mandy ml-1" />
                    </div>
                  </div>

                  {/* Corner Badge */}
                  <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-2 shadow-lg">
                    <div className="flex items-center text-gray-800">
                      <Icon name="clock" size="sm" className="mr-2" />
                      <span className="font-semibold">2h 30m</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedBackground>

      {/* Course Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-4" style={{ color: colors.mineShaft }}>
            {t('VIDEO_COURSE_PAGE.courseContent')}
          </h2>
          <div className="w-20 h-1 rounded-full" style={{ backgroundColor: 'var(--feel-primary)' }}></div>
        </div>

        <div className="max-w-6xl">
          {stateIsLoading(vcSelector) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Skeleton Loading */}
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Section Header Skeleton */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-xl animate-pulse"></div>
                      <div className="flex-1">
                        <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                      </div>
                    </div>
                  </div>

                  {/* Lessons List Skeleton */}
                  <div className="p-6">
                    <div className="space-y-3">
                      {[1, 2, 3].map(j => (
                        <div key={j} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                          <div className="flex-1">
                            <div className="h-3 bg-gray-200 rounded w-4/5 animate-pulse mb-1"></div>
                            <div className="h-2 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                          </div>
                          <div className="w-6 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Section Footer Skeleton */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <div className="flex items-center justify-end">
                      <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : vfiles.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vfiles.map((section, index) => (
                <div
                  key={section.sectionId}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 group h-full flex flex-col"
                >
                  {/* Section Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center space-x-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg"
                        style={{ backgroundColor: 'var(--feel-primary)' }}
                      >
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-mandy transition-colors duration-200">
                          {section.sectionName}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {section.files?.length || 0} {section.files?.length === 1 ? t('VIDEO_COURSE_PAGE.lesson') : t('VIDEO_COURSE_PAGE.lessons')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Lessons List */}
                  <div className="p-6 flex-1">
                    <div className="space-y-3">
                      {section.files?.slice(0, 3).map((file, fileIndex) => (
                        <div
                          key={file.id}
                          className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 group/item"
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white shadow-sm">
                            <Icon
                              name={file.type === 'video' ? 'play' : file.type === 'document' ? 'document' : 'image'}
                              size="sm"
                              className="text-gray-600 group-hover/item:text-mandy transition-colors duration-200"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate group-hover/item:text-mandy transition-colors duration-200">
                              {file.title}
                            </p>
                            {file.duration ? <p className="text-xs text-gray-500">{file.duration}</p> : null}
                          </div>
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                            <Icon name={isEnrolled ? 'check' : 'lock-closed'} size="xs" className={isEnrolled ? 'text-green-600' : 'text-gray-400'} />
                          </div>
                        </div>
                      ))}

                      {/* Show more indicator */}
                      {section.files?.length > 3 && (
                        <div className="text-center pt-2">
                          <span className="text-sm text-gray-500 font-medium">
                            +{section.files.length - 3} {t('VIDEO_COURSE_PAGE.moreLessons')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section Footer */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 mt-auto">
                    <div className="flex items-center justify-end">
                      <button
                        onClick={() => (isEnrolled ? openCoursePreviewPanel(section, undefined, vfiles) : handleViewDetails(section))}
                        className={`text-sm font-medium text-[var(--feel-primary)] hover:text-cabaret transition-colors duration-200`}
                      >
                        {(isEnrolled ? t('GENERAL.continue', 'Continua') : t('VIDEO_COURSE_PAGE.viewDetails')) + ' →'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Icon name="video-camera" size="xl" className="text-gray-400 mb-4 mx-auto" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('VIDEO_COURSE_PAGE.courseEmpty')}</h3>
              <p className="text-gray-600">{t('VIDEO_COURSE_PAGE.courseEmptyDescription')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Section Details Modal */}
      <Modal
        isOpen={showSectionModal}
        onClose={closeSectionModal}
        title={
          selectedSection ? (
            <div className="flex items-center space-x-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: 'var(--feel-primary)' }}
              >
                {vfiles.findIndex(s => s.sectionId === selectedSection.sectionId) + 1}
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: colors.mineShaft }}>
                  {selectedSection.sectionName}
                </h2>
                <p className="text-gray-500">
                  {selectedSection.files?.length || 0}{' '}
                  {selectedSection.files?.length === 1 ? t('VIDEO_COURSE_PAGE.lesson') : t('VIDEO_COURSE_PAGE.lessons')}
                </p>
              </div>
            </div>
          ) : null
        }
        className="max-w-4xl"
      >
        {selectedSection && (
          <>
            {/* Modal Content */}
            <div className="p-6">
              <div className="space-y-4">
                {selectedSection.files?.map(file =>
                  (() => {
                    const isLocked = checkIfLocked(file.free, isEnrolled);
                    const canPlay = file.transcoded || [FileTypes.document, FileTypes.image].includes(file.type);
                    const isDisabled = isLocked || !canPlay;
                    return (
                      <div
                        key={file.id}
                        onClick={() => !isDisabled && handlePreviewFile(file, selectedSection, vfiles)}
                        className={`flex items-center space-x-4 p-4 rounded-xl transition-colors duration-200 group ${
                          isDisabled ? 'bg-gray-50 opacity-70 cursor-not-allowed' : 'bg-gray-50 hover:bg-gray-100 cursor-pointer'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white shadow-sm">
                          <Icon
                            name={file.type === 'video' ? 'play' : file.type === 'document' ? 'document' : 'image'}
                            size="md"
                            className="text-gray-600 group-hover:text-mandy transition-colors duration-200"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-mandy transition-colors duration-200">{file.title}</h3>
                          <p className="text-sm text-gray-500">
                            {file.duration
                              ? `${file.duration} • ${
                                  file.type === 'video'
                                    ? t('VIDEO_COURSE_PAGE.video')
                                    : file.type === 'document'
                                    ? t('VIDEO_COURSE_PAGE.document')
                                    : t('VIDEO_COURSE_PAGE.image')
                                }`
                              : file.type === 'video'
                              ? t('VIDEO_COURSE_PAGE.video')
                              : file.type === 'document'
                              ? t('VIDEO_COURSE_PAGE.document')
                              : t('VIDEO_COURSE_PAGE.image')}
                          </p>
                          {file.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{file.description}</p>}
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <Icon name={isEnrolled ? 'check' : 'lock-closed'} size="sm" className={isEnrolled ? 'text-green-600' : 'text-gray-400'} />
                          </div>
                          {!isDisabled && <Icon name="chevron-right" size="sm" className="text-[var(--feel-primary)]" />}
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                  <span className="text-sm text-gray-500">{t('VIDEO_COURSE_PAGE.premiumContent')}</span>
                </div>
                <div className="flex space-x-3">
                  <button onClick={closeSectionModal} className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200">
                    {t('VIDEO_COURSE_PAGE.close')}
                  </button>
                  <button
                    onClick={() =>
                      isEnrolled ? (selectedSection ? openCoursePreviewPanel(selectedSection, undefined, vfiles) : null) : handleEnrollNow()
                    }
                    className="px-6 py-2 text-white rounded-lg hover:shadow-lg transition-all duration-200"
                    style={{ backgroundColor: 'var(--feel-primary)' }}
                  >
                    {isEnrolled ? t('GENERAL.continue', 'Continua') : t('VIDEO_COURSE_PAGE.enrollNow')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Modals */}
      <HtmlModal id="playVideoMedia" maxWidth="md">
        <HtmlVideoPlayer />
      </HtmlModal>
      <HtmlModal id="previewImage" maxWidth="lg">
        <MyImagePreview />
      </HtmlModal>

      <Modal
        isOpen={!!coursePreview}
        onClose={() => setCoursePreview(null)}
        headerClassName="items-center"
        headerContent={
          coursePreview ? (
            <div className="flex items-center gap-4 min-w-0 pr-2">
              <div
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-lg font-bold text-white shadow-sm"
                style={{ backgroundColor: 'var(--feel-primary)' }}
              >
                {coursePreview.sectionIndex}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold truncate" style={{ color: colors.mineShaft }}>
                  {coursePreview.title}
                </h2>
                <p className="text-sm text-gray-500">
                  {coursePreview.lessonCount} {coursePreview.lessonCount === 1 ? t('VIDEO_COURSE_PAGE.lesson') : t('VIDEO_COURSE_PAGE.lessons')}
                </p>
              </div>
            </div>
          ) : null
        }
        overlayClassName="!p-0"
        className="!max-w-none w-screen h-screen !max-h-screen rounded-none"
        contentClassName="!max-h-[calc(100vh-96px)]"
      >
        {coursePreview ? (
          <div className="h-[calc(100vh-96px)] p-3 sm:p-4">
            <MyCustomPreviewModal
              items={coursePreview.items}
              active={coursePreview.active}
              providerId={coursePreview.providerId}
              allowPreview={coursePreview.allowPreview}
              handleFileSeenUnseen={coursePreview.handleFileSeenUnseen}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default VideoCourseFiles;
